import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateFilterBar, { OutputMode, getDateRangeForMode } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { MapPin, TrendingUp, Building2, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { ColumnDefinition } from '../utils/exportToCsv'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'

interface CountyYoY {
  rank: number
  county: string
  current_count: number
  previous_count: number
  change: number
  change_percentage: number
  current_market_share: number
  previous_market_share: number
}

interface MunicipalityYoY {
  rank: number
  municipality_name: string
  municipality_number: string
  county: string
  current_count: number
  previous_count: number
  change: number
  change_percentage: number
  current_market_share: number
  previous_market_share: number
}

// Helper function for safe numeric conversion
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

// Helper to compute previous period dates (1 year back)
const computePreviousPeriod = (start: string, end: string) => {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  s.setFullYear(s.getFullYear() - 1)
  e.setFullYear(e.getFullYear() - 1)
  return {
    previous_start: s.toISOString().split('T')[0],
    previous_end: e.toISOString().split('T')[0],
  }
}

export default function GeographicAnalytics() {
  const { t } = useTranslation('analytics')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'counties' | 'municipalities'>('counties')
  const [counties, setCounties] = useState<CountyYoY[]>([])
  const [municipalities, setMunicipalities] = useState<MunicipalityYoY[]>([])
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<MunicipalityYoY[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCounty, setSelectedCounty] = useState<string>('')

  // Sort states
  const [countySortConfig, setCountySortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [municipalitySortConfig, setMunicipalitySortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('geographic-analytics')

  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()

  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('geographic-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const maxDataDate = useMaxDataDate()

  // Queried-state tracking -- only update when fetchData actually runs
  const [queriedOutputMode, setQueriedOutputMode] = useState<OutputMode>(outputMode)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)
  const [queriedStartDate, setQueriedStartDate] = useState(startDate.toISOString().split('T')[0])
  const [queriedEndDate, setQueriedEndDate] = useState(endDate.toISOString().split('T')[0])
  const [queriedSelectedCounty, setQueriedSelectedCounty] = useState(selectedCounty)

  useEffect(() => {
    fetchData()
  }, [selectedYear, vehicleFilter, maxSvvDate])

  const handleApplyFilters = () => {
    let s: Date, e: Date
    if (outputMode === 'custom') {
      if (!customStartDate || !customEndDate) return
      s = new Date(customStartDate)
      e = new Date(customEndDate)
    } else {
      const range = getDateRangeForMode(outputMode, maxSvvDate)
      s = new Date(range.startDate)
      e = new Date(range.endDate)
    }
    setStartDate(s)
    setEndDate(e)
    fetchData(s, e)
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const range = getDateRangeForMode(mode, maxSvvDate)
      const s = new Date(range.startDate)
      const e = new Date(range.endDate)
      setStartDate(s)
      setEndDate(e)
      fetchData(s, e)
    }
  }

  useEffect(() => {
    let filtered = municipalities

    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.municipality_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.county.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedCounty) {
      filtered = filtered.filter(m => m.county === selectedCounty)
    }

    setFilteredMunicipalities(filtered)
  }, [municipalities, searchTerm, selectedCounty])

  const fetchData = async (overrideStart?: Date, overrideEnd?: Date) => {
    try {
      setLoading(true)
      setError(null)

      // Snapshot queried state
      setQueriedOutputMode(outputMode)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)
      setQueriedStartDate((overrideStart ?? startDate).toISOString().split('T')[0])
      setQueriedEndDate((overrideEnd ?? endDate).toISOString().split('T')[0])
      setQueriedSelectedCounty(selectedCounty)

      const current_start = (overrideStart ?? startDate).toISOString().split('T')[0]
      const current_end = (overrideEnd ?? endDate).toISOString().split('T')[0]
      const { previous_start, previous_end } = computePreviousPeriod(current_start, current_end)

      const varebilParam = getVarebilParam()
      const params: any = { current_start, current_end, previous_start, previous_end }

      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const [countiesRes, municipalitiesRes] = await Promise.all([
        publicAPI.getCountyYoY(params),
        publicAPI.getMunicipalityYoY({ ...params, limit: 400 }),
      ])

      // Response structure: { data: [...] }
      const countiesData = countiesRes.data?.data || countiesRes.data || []
      const municipalitiesData = municipalitiesRes.data?.data || municipalitiesRes.data || []

      setCounties(Array.isArray(countiesData) ? countiesData : [])
      setMunicipalities(Array.isArray(municipalitiesData) ? municipalitiesData : [])
    } catch (err: any) {
      console.error('Geographic API error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('geo.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Change value rendering (matches BrandYoYComparison pattern)
  const renderChangeValue = (value: number | null, isPercentage: boolean = false) => {
    if (value === null) {
      return <span className="text-gray-400 dark:text-gray-500">N/A</span>
    }

    const numVal = toNumber(value)
    const color = numVal > 0
      ? 'text-green-600 dark:text-green-400'
      : numVal < 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-600 dark:text-gray-400'

    const prefix = numVal > 0 ? '+' : ''
    const suffix = isPercentage ? '%' : ''

    return (
      <span className={`font-semibold ${color}`}>
        {prefix}{numVal.toLocaleString('nb-NO', { minimumFractionDigits: isPercentage ? 1 : 0, maximumFractionDigits: isPercentage ? 1 : 0 })}{suffix}
      </span>
    )
  }

  // County sort handlers
  const handleCountySort = (key: keyof CountyYoY | 'change' | 'change_percentage') => {
    setCountySortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getCountySortIcon = (key: string) => {
    if (!countySortConfig || countySortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (countySortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  // Municipality sort handlers
  const handleMunicipalitySort = (key: keyof MunicipalityYoY | 'change' | 'change_percentage') => {
    setMunicipalitySortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const getMunicipalitySortIcon = (key: string) => {
    if (!municipalitySortConfig || municipalitySortConfig.key !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />
    if (municipalitySortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4" />
    return <ArrowDown className="h-4 w-4" />
  }

  // Sorted counties with useMemo
  const sortedCounties = useMemo(() => {
    if (!countySortConfig || !countySortConfig.key || !countySortConfig.direction) return counties

    return [...counties].sort((a, b) => {
      const key = countySortConfig.key as keyof CountyYoY
      const aVal = a[key]
      const bVal = b[key]

      // Handle string sorting
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return countySortConfig.direction === 'asc' ? cmp : -cmp
      }

      // Handle numeric sorting
      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)

      return countySortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [counties, countySortConfig])

  // Sorted municipalities with useMemo (sorts filteredMunicipalities)
  const sortedMunicipalities = useMemo(() => {
    if (!municipalitySortConfig || !municipalitySortConfig.key || !municipalitySortConfig.direction) return filteredMunicipalities

    return [...filteredMunicipalities].sort((a, b) => {
      const key = municipalitySortConfig.key as keyof MunicipalityYoY
      const aVal = a[key]
      const bVal = b[key]

      // Handle string sorting
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'no', { sensitivity: 'base' })
        return municipalitySortConfig.direction === 'asc' ? cmp : -cmp
      }

      // Handle numeric sorting
      const aNum = toNumber(aVal)
      const bNum = toNumber(bVal)

      return municipalitySortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
    })
  }, [filteredMunicipalities, municipalitySortConfig])

  // Column definitions for export
  const countyColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('common.rank') },
    { key: 'county', label: t('geo.county') },
    { key: 'current_count', label: t('common.count'), format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'previous_count', label: t('common.previousPeriod'), format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'change', label: '+/-', format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'change_percentage', label: t('common.changePct'), format: (val) => val !== null ? `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : 'N/A' },
    { key: 'current_market_share', label: t('geo.shareNowExport'), format: (val) => `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
    { key: 'previous_market_share', label: t('geo.sharePreviousExport'), format: (val) => `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
  ]

  const municipalityColumns: ColumnDefinition[] = [
    { key: 'rank', label: t('common.rank') },
    { key: 'municipality_name', label: t('geo.municipality') },
    { key: 'county', label: t('geo.county') },
    { key: 'current_count', label: t('common.count'), format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'previous_count', label: t('common.previousPeriod'), format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'change', label: '+/-', format: (val) => toNumber(val).toLocaleString('nb-NO') },
    { key: 'change_percentage', label: t('common.changePct'), format: (val) => val !== null ? `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : 'N/A' },
    { key: 'current_market_share', label: t('geo.shareNowExport'), format: (val) => `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
    { key: 'previous_market_share', label: t('geo.sharePreviousExport'), format: (val) => `${toNumber(val).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` },
  ]

  // Summary computations
  const totalCurrentCount = useMemo(() => counties.reduce((sum, c) => sum + toNumber(c.current_count), 0), [counties])
  const totalPreviousCount = useMemo(() => counties.reduce((sum, c) => sum + toNumber(c.previous_count), 0), [counties])
  const totalChangePercent = useMemo(() => {
    if (totalPreviousCount === 0) return null
    return ((totalCurrentCount - totalPreviousCount) / totalPreviousCount) * 100
  }, [totalCurrentCount, totalPreviousCount])

  const countyTotals = useMemo(() => {
    if (counties.length === 0) return null
    const totalCurrent = counties.reduce((s, c) => s + toNumber(c.current_count), 0)
    const totalPrevious = counties.reduce((s, c) => s + toNumber(c.previous_count), 0)
    const change = totalCurrent - totalPrevious
    const changePct = totalPrevious > 0 ? ((change / totalPrevious) * 100) : null
    return {
      current_count: totalCurrent,
      previous_count: totalPrevious,
      change,
      change_percentage: changePct,
    }
  }, [counties])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  const uniqueCounties = Array.from(new Set(municipalities.map(m => m.county))).sort()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('geo.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('geo.subtitle')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('common.sourceData')}</p>
      </div>

      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilters}
        maxDataDate={maxDataDate}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />

      {/* Query Explanation Box */}
      <QueryExplanationBox
        maxDataDate={maxDataDate}
        outputMode={queriedOutputMode}
        vehicleFilter={queriedVehicleFilter}
        postnummerFilter={queriedPostnummer}
        periodLabel={t(`outputModes.${queriedOutputMode}`)}
        startDate={queriedStartDate}
        endDate={queriedEndDate}
        drillDownLabel={queriedSelectedCounty ? t('geo.municipalitiesIn', { county: queriedSelectedCounty }) : null}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('geo.counties')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{counties.length}</p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('geo.municipalities')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{municipalities.length}</p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('geo.totalCurrentPeriod')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{totalCurrentCount.toLocaleString('nb-NO')}</p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{t('geo.changeVsLastYear')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {totalChangePercent !== null
                  ? <span className={totalChangePercent > 0 ? 'text-green-600 dark:text-green-400' : totalChangePercent < 0 ? 'text-red-600 dark:text-red-400' : ''}>
                      {totalChangePercent > 0 ? '+' : ''}{totalChangePercent.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                    </span>
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
        <button
          onClick={() => setActiveTab('counties')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'counties'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          {t('geo.countiesTab', { n: counties.length })}
        </button>
        <button
          onClick={() => setActiveTab('municipalities')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'municipalities'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          {t('geo.municipalitiesTab', { n: municipalities.length })}
        </button>
      </div>

      {/* Counties Table */}
      {activeTab === 'counties' && (
        <>
          {/* Export and Clear Sorting */}
          <div className="flex justify-between items-center mb-4">
            <div>
              {countySortConfig && countySortConfig.key && (
                <div className="card px-4 py-2">
                  <button
                    onClick={() => setCountySortConfig(null)}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  >
                    {t('common.resetSort')}
                  </button>
                </div>
              )}
            </div>
            <ExportButtons
              data={sortedCounties}
              columns={countyColumns}
              metadata={{
                title: t('geo.exportCounties'),
                dateRange: `${startDate.toLocaleDateString('nb-NO')} - ${endDate.toLocaleDateString('nb-NO')}`,
                filters: minePostnummer ? [t('geo.postnummer', { value: minePostnummer })] : undefined,
                explanationText: buildExplanationText({
                  maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                  postnummerFilter: queriedPostnummer, periodLabel: t(`outputModes.${queriedOutputMode}`),
                  startDate: queriedStartDate, endDate: queriedEndDate,
                }),
              }}
            />
          </div>

          <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th
                    className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('rank')}
                    title={t('common.originalRanking')}
                  >
                    <div className="flex items-center justify-center">
                      <span>#</span>
                      {getCountySortIcon('rank')}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('county')}
                  >
                    <div className="flex items-center justify-between">
                      <span>{t('geo.county')}</span>
                      {getCountySortIcon('county')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('current_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.count')}</span>
                      {getCountySortIcon('current_count')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('previous_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.previousPeriod')}</span>
                      {getCountySortIcon('previous_count')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('change')}
                  >
                    <div className="flex items-center justify-end">
                      <span>+/-</span>
                      {getCountySortIcon('change')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('change_percentage')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.changePct')}</span>
                      {getCountySortIcon('change_percentage')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('current_market_share')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('geo.shareNow')}</span>
                      {getCountySortIcon('current_market_share')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleCountySort('previous_market_share')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('geo.sharePrevious')}</span>
                      {getCountySortIcon('previous_market_share')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {countyTotals && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-b-2 border-primary-300 dark:border-primary-600">
                    <td className="text-center">
                      <span className="text-primary-600 dark:text-primary-400 font-bold">&sum;</span>
                    </td>
                    <td className="font-bold text-primary-700 dark:text-primary-300">{t('geo.norwayTotal')}</td>
                    <td className="text-right font-bold">{countyTotals.current_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right font-bold">{countyTotals.previous_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right">{renderChangeValue(countyTotals.change)}</td>
                    <td className="text-right">{renderChangeValue(countyTotals.change_percentage, true)}</td>
                    <td className="text-right text-sm font-bold">100,0 %</td>
                    <td className="text-right text-sm font-bold">100,0 %</td>
                  </tr>
                )}
                {sortedCounties.map((county) => (
                  <tr key={county.county}>
                    <td className="text-center text-gray-700 dark:text-gray-300">
                      {county.rank}
                    </td>
                    <td className="font-semibold">{county.county}</td>
                    <td className="text-right font-medium">{toNumber(county.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right">{toNumber(county.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right">
                      {renderChangeValue(county.change)}
                    </td>
                    <td className="text-right">
                      {renderChangeValue(county.change_percentage, true)}
                    </td>
                    <td className="text-right text-sm">
                      {toNumber(county.current_market_share).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                    </td>
                    <td className="text-right text-sm">
                      {toNumber(county.previous_market_share).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Municipalities Table */}
      {activeTab === 'municipalities' && (
        <>
          {/* Filters */}
          <div className="card p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('geo.searchPlaceholder')}
              />
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="input-field"
              >
                <option value="">{t('geo.allCounties')}</option>
                {uniqueCounties.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Export and Clear Sorting */}
          <div className="flex justify-between items-center mb-4">
            <div>
              {municipalitySortConfig && municipalitySortConfig.key && (
                <div className="card px-4 py-2">
                  <button
                    onClick={() => setMunicipalitySortConfig(null)}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  >
                    {t('common.resetSort')}
                  </button>
                </div>
              )}
            </div>
            <ExportButtons
              data={sortedMunicipalities}
              columns={municipalityColumns}
              metadata={{
                title: t('geo.exportMunicipalities'),
                dateRange: `${startDate.toLocaleDateString('nb-NO')} - ${endDate.toLocaleDateString('nb-NO')}`,
                filters: [
                  ...(minePostnummer ? [t('geo.postnummer', { value: minePostnummer })] : []),
                  ...(searchTerm ? [t('geo.search', { value: searchTerm })] : []),
                  ...(selectedCounty ? [t('geo.countyFilter', { value: selectedCounty })] : []),
                ].filter(f => f.length > 0),
                explanationText: buildExplanationText({
                  maxDataDate, outputMode: queriedOutputMode, vehicleFilter: queriedVehicleFilter,
                  postnummerFilter: queriedPostnummer, periodLabel: t(`outputModes.${queriedOutputMode}`),
                  startDate: queriedStartDate, endDate: queriedEndDate,
                  drillDownLabel: queriedSelectedCounty ? t('geo.municipalitiesIn', { county: queriedSelectedCounty }) : null,
                }),
              }}
            />
          </div>

          <div className="card overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th
                    className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('rank')}
                    title={t('common.originalRanking')}
                  >
                    <div className="flex items-center justify-center">
                      <span>#</span>
                      {getMunicipalitySortIcon('rank')}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('municipality_name')}
                  >
                    <div className="flex items-center justify-between">
                      <span>{t('geo.municipality')}</span>
                      {getMunicipalitySortIcon('municipality_name')}
                    </div>
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('county')}
                  >
                    <div className="flex items-center justify-between">
                      <span>{t('geo.county')}</span>
                      {getMunicipalitySortIcon('county')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('current_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.count')}</span>
                      {getMunicipalitySortIcon('current_count')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('previous_count')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.previousPeriod')}</span>
                      {getMunicipalitySortIcon('previous_count')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('change')}
                  >
                    <div className="flex items-center justify-end">
                      <span>+/-</span>
                      {getMunicipalitySortIcon('change')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('change_percentage')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('common.changePct')}</span>
                      {getMunicipalitySortIcon('change_percentage')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('current_market_share')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('geo.shareNow')}</span>
                      {getMunicipalitySortIcon('current_market_share')}
                    </div>
                  </th>
                  <th
                    className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => handleMunicipalitySort('previous_market_share')}
                  >
                    <div className="flex items-center justify-end">
                      <span>{t('geo.sharePrevious')}</span>
                      {getMunicipalitySortIcon('previous_market_share')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMunicipalities.map((muni) => (
                  <tr key={muni.municipality_number}>
                    <td className="text-center text-gray-700 dark:text-gray-300">
                      {muni.rank}
                    </td>
                    <td className="font-medium">{muni.municipality_name}</td>
                    <td>
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-sm">
                        {muni.county}
                      </span>
                    </td>
                    <td className="text-right font-medium">{toNumber(muni.current_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right">{toNumber(muni.previous_count).toLocaleString('nb-NO')}</td>
                    <td className="text-right">
                      {renderChangeValue(muni.change)}
                    </td>
                    <td className="text-right">
                      {renderChangeValue(muni.change_percentage, true)}
                    </td>
                    <td className="text-right text-sm">
                      {toNumber(muni.current_market_share).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                    </td>
                    <td className="text-right text-sm">
                      {toNumber(muni.previous_market_share).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedMunicipalities.length === 0 && (
              <div className="p-12 text-center">
                <MapPin className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 text-lg">{t('geo.noMunicipalities')}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
